import { useEffect, useCallback, useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';

interface UseAxissWidgetOptions {
  apiKey?: string; // Could be from env var
  hideBubble?: boolean;
}

interface AxissUser {
  email: string;
  name?: string;
}

declare global {
  interface Window {
    Axiss?: {
      init: (options: { apiKey: string; hideBubble?: boolean }) => void;
      open: (view?: string) => void;
      /** Opens the help center on one article (widget builds with the help center). */
      openArticle?: (slug: string) => void;
      close: () => void;
      setUser: (user: AxissUser | null) => void;
      on: (event: string, callback: (data: any) => void) => void;
      off: (event: string, callback: (data: any) => void) => void;
    };
  }
}

export function useAxissWidget(options?: UseAxissWidgetOptions) {
  const { user } = useAuthStore();
  const bakedKey = options?.apiKey || import.meta.env.VITE_AXISS_API_KEY || '';
  const hideBubble = options?.hideBubble || false;
  // Resolved key/url. Start from the build-baked value so a missing/failed
  // runtime config endpoint degrades to the previous behavior (zero regression
  // for sites provisioned via the build-time flow).
  const [apiKey, setApiKey] = useState(bakedKey);
  const [apiUrl, setApiUrl] = useState<string>(
    import.meta.env.VITE_AXISS_API_URL || 'https://app.axiss.support/api/widget'
  );
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isAxissReady, setIsAxissReady] = useState(false);

  // Resolve the widget key at runtime from this site's backend (admin →
  // Integrations → AXISS). Precedence:
  //   - enabled + key  → use the tenant-managed key (instant, no rebuild)
  //   - managed + off  → admin explicitly disabled → no widget (overrides baked)
  //   - not managed    → fall back to the build-baked key
  // An explicit options.apiKey always wins and skips the fetch.
  // Note: changes apply on next page load — the widget script is injected once
  // and persists across route changes.
  useEffect(() => {
    if (options?.apiKey) return;
    let cancelled = false;
    const base = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';
    fetch(`${base}/integrations/axiss/config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cancelled || !cfg) return;
        if (cfg.enabled && cfg.apiKey) {
          setApiKey(cfg.apiKey);
          if (cfg.apiUrl) setApiUrl(cfg.apiUrl);
        } else if (cfg.managed) {
          setApiKey('');
        } else {
          setApiKey(bakedKey);
        }
      })
      .catch(() => {
        // Endpoint unreachable → keep the build-baked key.
      });
    return () => {
      cancelled = true;
    };
  }, [options?.apiKey, bakedKey]);

  // Load widget script dynamically
  useEffect(() => {
    if (typeof window === 'undefined' || !apiKey) {
      // No key yet (runtime config still resolving, or AXISS not enabled).
      return;
    }

    // Check if script already loaded
    const scriptId = 'axiss-widget-script';
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      setIsScriptLoaded(true);
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://widget.axiss.support/axiss-widget.js';
    script.setAttribute('data-axiss-key', apiKey);
    script.setAttribute('data-axiss-url', apiUrl);
    const turnstileKey = import.meta.env.VITE_AXISS_TURNSTILE_SITE_KEY || '0x4AAAAAACgPDjy7QK2EQ72O';
    if (turnstileKey) {
      script.setAttribute('data-axiss-turnstile-key', turnstileKey);
    }
    if (hideBubble) {
      script.setAttribute('data-axiss-hide-bubble', 'true');
    }
    script.async = true;

    script.onload = () => {
      setIsScriptLoaded(true);
    };

    script.onerror = () => {
      console.error('Failed to load Axiss widget script');
    };

    document.body.appendChild(script);

    return () => {
      // Don't remove script on unmount - widget persists across route changes
    };
  }, [apiKey, apiUrl, hideBubble]);

  // Wait for Axiss to be available after script loads
  useEffect(() => {
    if (!isScriptLoaded) return;

    const checkAxiss = setInterval(() => {
      if (window.Axiss) {
        clearInterval(checkAxiss);
        setIsAxissReady(true);

        // Initialize widget if needed (script auto-initializes, but we can call init for config)
        // window.Axiss.init({ apiKey, hideBubble });
      }
    }, 100);

    return () => clearInterval(checkAxiss);
  }, [isScriptLoaded, apiKey, hideBubble]);

  // Sync user identity when auth changes
  useEffect(() => {
    if (!isAxissReady || !window.Axiss) return;

    if (user) {
      window.Axiss.setUser({
        email: user.email,
        name: user.firstName ? `${user.firstName} ${user.lastName}`.trim() : undefined
      });
    }
  }, [isAxissReady, user]);

  // Listen to widget events for analytics
  useEffect(() => {
    if (!isAxissReady || !window.Axiss) return;

    const handleOpen = (data: { view: string }) => {
      // Track widget open event
      console.log('Axiss widget opened to:', data.view);
      // You could send to analytics service here
    };

    const handleClose = () => {
      console.log('Axiss widget closed');
    };

    window.Axiss.on('open', handleOpen);
    window.Axiss.on('close', handleClose);

    return () => {
      if (window.Axiss) {
        window.Axiss.off('open', handleOpen);
        window.Axiss.off('close', handleClose);
      }
    };
  }, [isAxissReady]);

  // Provide control methods
  const openWidget = useCallback((view?: string) => {
    if (window.Axiss) {
      window.Axiss.open(view as any);
    } else {
      console.warn('Axiss widget not ready');
    }
  }, []);

  const closeWidget = useCallback(() => {
    if (window.Axiss) window.Axiss.close();
  }, []);

  return { openWidget, closeWidget, isReady: isAxissReady };
}